import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mediaUrl, type ItemMedia } from '../api';

type Props = {
  media: ItemMedia[];
};

/**
 * Karrusel der viser ét billede ad gangen med pile + dot-indicators.
 * Hvis der kun er ét billede, vises det uden navigation.
 * Klik på billede åbner det fulde billede i en ny fane.
 */
export default function ImageCarousel({ media }: Props) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);

  if (media.length === 0) return null;

  const current = media[index] ?? media[0]!;
  const hasMultiple = media.length > 1;

  function prev() {
    setIndex((i) => (i - 1 + media.length) % media.length);
  }
  function next() {
    setIndex((i) => (i + 1) % media.length);
  }

  return (
    <div className="space-y-3">
      <div className="relative bg-slate-100 rounded-lg overflow-hidden aspect-[4/3]">
        <a
          href={mediaUrl(current)}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full h-full"
        >
          <img
            src={mediaUrl(current)}
            alt=""
            className="w-full h-full object-contain"
          />
        </a>
        {hasMultiple && (
          <>
            <button
              type="button"
              onClick={prev}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-900 rounded-full w-9 h-9 flex items-center justify-center shadow"
              aria-label={t('media.previous')}
            >
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 4L6 8l4 4"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={next}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-900 rounded-full w-9 h-9 flex items-center justify-center shadow"
              aria-label={t('media.next')}
            >
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 4l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs rounded-full px-2 py-0.5">
              {index + 1} / {media.length}
            </div>
          </>
        )}
      </div>

      {hasMultiple && (
        <div className="flex justify-center gap-2 flex-wrap">
          {media.map((m, i) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setIndex(i)}
              className={`w-14 h-14 rounded overflow-hidden border-2 transition ${
                i === index ? 'border-slate-900' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
              aria-label={`${t('media.gotoImage')} ${i + 1}`}
            >
              <img
                src={mediaUrl(m, { thumbnail: !!m.thumbnailKey })}
                alt=""
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
